import { ref, computed } from 'vue';
import { getAllSnmps, createSNMP, updateSNMP, deleteSNMP } from '@/services/snmpService';
import { getAllSnmpWalks, createSnmpWalk, updateSnmpWalk, deleteSnmpWalk } from '@/services/snmpwalkoidService';
import { useStore } from '@/store';
import { useMultiTenant } from '@/composables/useMultiTenant';
import { extractErrorMessage } from '@/utils/errorMessages';

export function useSnmpManagement(parentType = 'site', parentId, orgId, schemaName  = null) {
  const tenant = useMultiTenant();
  
  const showSnmpModal = ref(false);
  const showSnmpDropdown = ref(false);
  const showAddSnmpModal = ref(false);
  const showLinkButton = ref(true);
  const selectedSnmp = ref(null);
  const selectedWalkOIDs = ref([]);
  const selectedSnmpTargets = ref([]);
  const editingSnmp = ref(false);
  const snmps = ref([]);
  const availableOrgSnmps = ref([]);
  const snmpErrors = ref({});
  const techTypes = ['fortigate', 'cisco'];
  const errorMessage = ref(null);


  const	isStaff = computed(() => useStore.isStaff());
	const canAddSnmp = computed(() => useStore.hasPermission('Can add snmp'));
  const canChangeSnmp = computed(() => useStore.hasPermission('Can change snmp') || isStaff.value);
  const canDeleteSnmp = computed(() => useStore.hasPermission('Can delete snmp'));
  const canViewSnmp = computed(() => useStore.hasPermission('Can view snmp'));

  const fetchSnmps = async () => {
    try {
      const { valid, schemaName } = getSchemaNameForOperation();
      if (!valid) {
        errorMessage.value = 'Malformed request for fetching SNMP configurations';
        return;
      }
      // filter params based on parentType eg {site: 3}
      const filterParams = { [parentType]: parentId.value };
      snmps.value = await getAllSnmps(schemaName, filterParams);

      // For site/probe: also fetch available org SNMPs not yet linked
      if (parentType !== 'org' && orgId?.value) {
        const availableParams = { [`available_for_${parentType}`]: parentId.value };
        availableOrgSnmps.value = await getAllSnmps(schemaName, availableParams);
      }
    } catch (error) {
      console.error('Error fetching SNMPs:', error);
      errorMessage.value = extractErrorMessage(error, 'Failed to fetch SNMP configurations');
    }
  };

  // Separate parent specific SNMPs 
  const ownedSnmps = computed(() => {
    if (parentType === 'org') {
      // Org-level: all are "owned"
      return snmps.value;
    }
    // Site/Probe: owned means site/probe field is set to this ID
    return snmps.value.filter(snmp => snmp[parentType] === parentId.value);
  });

	// Org SNMPs linked site/probe 
  const linkedOrgSnmps = computed(() => {
    if (parentType === 'org') {
      return [];
    }
    // Site/Probe: linked means org is set but site/probe is NOT this ID
    return snmps.value.filter(snmp => 
			snmp.org !== null && 
			snmp.site === null && 
			snmp.probe === null
		);
  });

  const getWalkOIDsForSnmp = (snmpId) => {
    const snmp = snmps.value.find(s => s.id === snmpId);
  		return snmp?.walk_oids || [];
  };

  const addSnmp = () => {
    const snmpData = {
      tech_type: '',
      community_string: '',
      scrape_interval: 60,
      scrape_timeout: 30,
      org: parentType === 'org' ? parentId.value : null,
      site: parentType === 'site' ? parentId.value : null,
      probe: parentType === 'probe' ? parentId.value : null,
      id: null
    };
    showLinkButton.value = false;
    selectedSnmp.value = snmpData;
    selectedWalkOIDs.value = [];
    selectedSnmpTargets.value = [];
    editingSnmp.value = true;
    showLinkButton.value = false;
    showSnmpModal.value = true;
  };

  const editSnmp = (snmp) => {
    selectedSnmp.value = { 
			...snmp,
			isOrgLevel: snmp.org !== null && snmp.site === null && snmp.probe === null
		};
    selectedWalkOIDs.value = getWalkOIDsForSnmp(snmp.id);
    selectedSnmpTargets.value = snmp.snmp_targets ? [...snmp.snmp_targets] : [];
    editingSnmp.value = true;
    showLinkButton.value = false;
    showSnmpModal.value = true;
    showSnmpDropdown.value = false;
  };

  const viewSnmp = (snmp) => {
    selectedSnmp.value = { 
			...snmp,
			isOrgLevel: snmp.org !== null && snmp.site === null && snmp.probe === null
		};
    selectedWalkOIDs.value = getWalkOIDsForSnmp(snmp.id);
    selectedSnmpTargets.value = snmp.snmp_targets || [];
    editingSnmp.value = false; // View only 
    showLinkButton.value = false;
    showSnmpModal.value = true;
    showAddSnmpModal.value = false;
  };

  const cancelSnmp = () => {
    showSnmpModal.value = false;
    selectedSnmp.value = null;
    selectedWalkOIDs.value = [];
    selectedSnmpTargets.value = [];
    editingSnmp.value = false;
    showLinkButton.value = true;
    snmpErrors.value = {};
    errorMessage.value = null;
  };

  const previewSnmp = (snmp) => {
    selectedSnmp.value = {
			...snmp,
			isOrgLevel: snmp.org !== null && snmp.site === null && snmp.probe === null
		};
    // The org SNMP comes from availableOrgSnmps (not snmps.value), so read its
    // walk OIDs straight off the object rather than looking it up by id.
    selectedWalkOIDs.value = snmp.walk_oids || [];
    selectedSnmpTargets.value = snmp.snmp_targets || [];
    editingSnmp.value = false; 
    showLinkButton.value = true;
    showSnmpModal.value = true;
    showAddSnmpModal.value = false;
  };


  const linkOrgSnmp = async (orgSnmpId) => {
    errorMessage.value = null;
    try {
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) {
        errorMessage.value = validation.error;
        return;
      }

      // Remove from exclusions so the org SNMP propagates again
      const updateData = {};
      if (parentType === 'site') {
        updateData.remove_from_excluded_sites = [parentId.value];
      } else if (parentType === 'probe') {
        updateData.remove_from_excluded_probes = [parentId.value];
      }

      await updateSNMP(orgSnmpId, updateData, validation.schemaName);
      await fetchSnmps();
      showAddSnmpModal.value = false;
      cancelSnmp();
    } catch (error) {
      console.error('Error linking org SNMP:', error);
      errorMessage.value = extractErrorMessage(error, 'Failed to link organization SNMP configuration');
    }
  };

  const unlinkOrgSnmp = async (orgSnmpId) => {
    errorMessage.value = null;
    try {
      const { valid, schemaName } = getSchemaNameForOperation();
      if (!valid) {
        errorMessage.value = 'Malformed request for updating SNMP operation';
        return;
      }

      // Add to exclusions so the org SNMP no longer propagates here
      const updateData = {};
      if (parentType === 'site') {
        updateData.add_to_excluded_sites = [parentId.value];
      } else if (parentType === 'probe') {
        updateData.add_to_excluded_probes = [parentId.value];
      }

      await updateSNMP(orgSnmpId, updateData, schemaName);
      await fetchSnmps();
    } catch (error) {
      console.error('Error unlinking org SNMP:', error);
      errorMessage.value = extractErrorMessage(error, 'Failed to unlink organization SNMP configuration');
    }
  };

  const toggleAddSnmpSelector = () => {
    showAddSnmpModal.value = !showAddSnmpModal.value;
  };

  const clearSnmpError = (fieldName) => {
    if (snmpErrors.value[fieldName]) {
      delete snmpErrors.value[fieldName];
    }
  };

  const clearAllSnmpErrors = () => {
    snmpErrors.value = {};
  };

  const handleValidationErrors = (errors) => {
    snmpErrors.value = errors;
  };


  const handleSnmpSave = async (data) => {
    errorMessage.value = null;
    try {
      snmpErrors.value = {};
      const { valid, schemaName } = getSchemaNameForOperation();
      if (!valid) {
        errorMessage.value = 'Malformed request for SNMP save operation';
        return;
      }
      let savedSnmp;

      const snmpPayload = {
        ...data.snmp,
        snmp_targets: data.snmpTargets || [] 
      };
      if (data.snmp.id) {
        savedSnmp = await updateSNMP(data.snmp.id, snmpPayload, schemaName);
      } else {
        savedSnmp = await createSNMP(snmpPayload, schemaName);
      }

      // Handle walk OIDs
      await saveWalkOIDs(savedSnmp.id, data.walkOIDs, schemaName);

      await fetchSnmps();
      cancelSnmp();
      
    } catch (error) {
      handleSaveError(error);
    }
  };

  const saveWalkOIDs = async (snmpId, walkOIDs, schemaName) => {
    // Find which walk OIDs to delete
    const currentWalkOIDIds = walkOIDs.filter(w => w.id).map(w => w.id);
    const originalWalkOIDIds = selectedWalkOIDs.value.filter(w => w.id).map(w => w.id);
    const toDelete = originalWalkOIDIds.filter(id => !currentWalkOIDIds.includes(id));
    
    // Delete removed walk OIDs
    for (const walkId of toDelete) {
      await deleteSnmpWalk(walkId, schemaName);
    }

    // Create or update walk OIDs
    for (const walk of walkOIDs) {
      const walkData = {
        value: walk.value,
        snmp: snmpId,
        org: parentType === 'org' ? parentId.value : null,
        site: parentType === 'site' ? parentId.value : null,
        probe: parentType === 'probe' ? parentId.value : null
      };
      
      if (walk.id) {
        await updateSnmpWalk(walk.id, walkData, schemaName);
      } else {
        await createSnmpWalk(walkData, schemaName);
      }
    }
  };


  const handleSaveError = (error) => {
    const errData = error?.response?.data;
    // Extract field-level validation errors into snmpErrors for inline display
    if (errData && typeof errData === 'object' && !Array.isArray(errData)) {
      Object.keys(errData).forEach(key => {
        if (key !== 'detail' && key !== 'non_field_errors' && errData[key]?.[0]) {
          snmpErrors.value[key] = errData[key][0];
        }
      });
    }
    console.error('Error saving SNMP:', error);
    errorMessage.value = extractErrorMessage(error, 'Failed to save SNMP configuration');
  };


  const handleSnmpDelete = async (snmpId) => {
    errorMessage.value = null;
    try {
      const { valid, schemaName } = getSchemaNameForOperation();
      if (!valid) {
        errorMessage.value = 'Malformed request for SNMP delete operation';
        return;
      }

      await deleteSNMP(snmpId, schemaName);
      await fetchSnmps();
      cancelSnmp();
    } catch (error) {
      console.error('Error deleting SNMP:', error);
      errorMessage.value = error?.response?.data?.detail
        ?? 'Failed to delete SNMP configuration';
    }
  };

	const showAddSnmpSelector = () => {
     // The selector modal only adds value when there are existing org SNMPs to
     // link to. When there's nothing to link (org level, or no available org
     // SNMPs), skip it and go straight to creating a new config.
     const canLinkExisting = parentType !== 'org' && availableOrgSnmps.value.length > 0;
     if (!canLinkExisting) {
       createNewSnmp();
       return;
     }
     showAddSnmpModal.value = true;
   };

	const createNewSnmp = () => {
     showAddSnmpModal.value = false;  
     addSnmp();  
  };
  const getSchemaNameForOperation = () => {
    if (parentType !== 'org') {
      const validation = tenant.validateSchemaForOperation();
      return { valid: validation.valid, schemaName: validation.schemaName ?? null };
    }
    if (parentType === 'org' && schemaName) {
      return { valid: true, schemaName };
    }
    return { valid: false, schemaName: null };
  };

  return {
    // State
    showSnmpModal,
    showSnmpDropdown,
    showAddSnmpModal,
    selectedSnmp,
    selectedWalkOIDs,
    selectedSnmpTargets,
    editingSnmp,
    snmps,
    availableOrgSnmps,
    snmpErrors,
    techTypes,
    errorMessage,
    showLinkButton,
    
    // Computed 
		isStaff,
    canAddSnmp,
    canChangeSnmp,
    canDeleteSnmp,
    canViewSnmp,
    ownedSnmps,
    linkedOrgSnmps,
    
    // Methods
    fetchSnmps,
    getWalkOIDsForSnmp,
    addSnmp,
    editSnmp,
    viewSnmp,
    previewSnmp,
    linkOrgSnmp,
    unlinkOrgSnmp,
    toggleAddSnmpSelector,
		showAddSnmpSelector,        
		createNewSnmp,              
    cancelSnmp,
    clearSnmpError,
    clearAllSnmpErrors,
    handleValidationErrors,
    handleSnmpSave,
    handleSnmpDelete
  };
}